/**
 * Persistent capture store.
 *
 * iOS Safari and the Despia native WebView aggressively suspend (and often
 * fully reload) the page when `<input type="file" capture>` opens the system
 * camera. Any captured photo held only in React state is lost on resume,
 * which is what makes the pest scan / invoice scan look like "nothing
 * happens, sent back to the menu".
 *
 * This module persists captured `File` blobs in IndexedDB under a short
 * scope key, so on rehydration the page can recover the exact same file
 * the user just took and the user can submit / replace / delete it.
 *
 * - TTL: entries older than 30 minutes are auto-purged on read.
 * - Falls back to in-memory storage when IndexedDB is unavailable
 *   (private mode, very old browsers).
 * - Multi-file scopes (invoice scan) are supported via `setMany` / `getMany`.
 *
 * NEVER stores anything sensitive — only the binary photo blob and its name.
 */

const DB_NAME = "ulture_capture";
const STORE = "pending";
const VERSION = 1;
const TTL_MS = 30 * 60 * 1000;

export type CaptureScope =
  | "pest-scan"
  | "worker-scouting"
  | "worker-scouting-evidence"
  | "worker-scouting-scan"
  | "invoice-scan"
  | "product-label-scan"
  | "transaction-invoice";

interface StoredEntry {
  scope: string;
  files: { name: string; type: string; blob: Blob }[];
  savedAt: number;
}

// In-memory fallback shared across the app.
const memoryStore = new Map<string, StoredEntry>();

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    try {
      const req = indexedDB.open(DB_NAME, VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "scope" });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      const req = fn(store);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function isFresh(entry: StoredEntry | null | undefined): entry is StoredEntry {
  if (!entry) return false;
  return Date.now() - entry.savedAt < TTL_MS;
}

function entryToFiles(entry: StoredEntry): File[] {
  return entry.files.map(
    (f) => new File([f.blob], f.name, { type: f.type || "image/jpeg" }),
  );
}

export async function setPendingCapture(
  scope: CaptureScope,
  files: File[],
): Promise<void> {
  const entry: StoredEntry = {
    scope,
    files: files.map((f) => ({
      name: f.name || "photo.jpg",
      type: f.type || "image/jpeg",
      blob: f,
    })),
    savedAt: Date.now(),
  };
  memoryStore.set(scope, entry);
  await withStore("readwrite", (s) => s.put(entry));
}

export async function getPendingCapture(
  scope: CaptureScope,
): Promise<File[] | null> {
  // Try IDB first, fall back to memory.
  const fromIdb = (await withStore<StoredEntry>("readonly", (s) =>
    s.get(scope) as IDBRequest<StoredEntry>,
  )) as StoredEntry | null;
  let entry: StoredEntry | null = fromIdb;
  if (!isFresh(entry)) {
    entry = memoryStore.get(scope) ?? null;
    if (!isFresh(entry)) {
      // Clean stale entry
      if (fromIdb) await withStore("readwrite", (s) => s.delete(scope));
      memoryStore.delete(scope);
      return null;
    }
  }
  return entryToFiles(entry);
}

export async function clearPendingCapture(scope: CaptureScope): Promise<void> {
  memoryStore.delete(scope);
  await withStore("readwrite", (s) => s.delete(scope));
}

export async function appendPendingCapture(
  scope: CaptureScope,
  file: File,
): Promise<File[]> {
  const existing = (await getPendingCapture(scope)) ?? [];
  const next = [...existing, file];
  await setPendingCapture(scope, next);
  return next;
}
